import React, { useState, useEffect } from 'react';
import { Modal, Button, Tile, InlineNotification } from '@carbon/react';
import { Bluetooth, Scan, Checkmark } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import bluetoothService from '../services/bluetoothService';

const BluetoothScanModal = ({ isOpen, onClose, onDeviceSelect }) => {
  const { theme } = useTheme();
  const [scanning, setScanning] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [error, setError] = useState('');
  const [bluetoothAvailable, setBluetoothAvailable] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Check Bluetooth availability when modal opens
  useEffect(() => {
    if (isOpen) {
      checkBluetoothAvailability();
    }
  }, [isOpen]);

  const checkBluetoothAvailability = async () => {
    const availability = await bluetoothService.isBluetoothAvailable();
    setBluetoothAvailable(availability);
    if (!availability.available) {
      setError(availability.reason);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    setError('');
    setDevices([]);

    try {
      // Request device from browser picker
      const device = await bluetoothService.requestDevice();

      if (device) {
        // Parse device information
        const deviceInfo = {
          id: device.id,
          name: device.name || 'Unknown Device',
          gatt: device.gatt,
          device: device, // Keep reference to original device
        };

        // Check if it's a sensor device
        if (bluetoothService.isSensorDevice(deviceInfo)) {
          // Add to devices list
          setDevices(prev => {
            // Check if already in list
            const exists = prev.some(d => d.id === deviceInfo.id);
            if (!exists) {
              return [...prev, deviceInfo];
            }
            return prev;
          });

          // Auto-select the device
          setSelectedDevice(deviceInfo);
        } else {
          setError('This device does not appear to be a sensor. Please try scanning again and select a temperature/humidity sensor.');
        }
      }
    } catch (err) {
      console.error('Bluetooth scan error:', err);
      if (err.message === 'No device selected') {
        // User cancelled - don't show error
        setError('');
      } else {
        setError(err.message || 'Failed to scan for devices. Please try again.');
      }
    } finally {
      setScanning(false);
    }
  };

  const handleDeviceClick = (device) => {
    setSelectedDevice(device);
  };

  const handleConnect = async () => {
    if (!selectedDevice) return;

    setConnecting(true);
    setError('');

    try {
      let realMacAddress = null;

      // Try to read the actual MAC address from device characteristics
      if (selectedDevice.device) {
        console.log('Attempting to read MAC address from device...');
        realMacAddress = await bluetoothService.readMacAddress(selectedDevice.device);

        if (realMacAddress) {
          console.log('Successfully read real MAC address:', realMacAddress);
        } else {
          console.log('Could not read MAC address from characteristics, using device ID');
        }

        // Disconnect after reading info
        if (selectedDevice.device.gatt && selectedDevice.device.gatt.connected) {
          try {
            await selectedDevice.device.gatt.disconnect();
          } catch (disconnectErr) {
            console.warn('Disconnect warning:', disconnectErr);
          }
        }
      }

      // Parse device data
      const parsedData = {
        id: selectedDevice.id,
        name: selectedDevice.name,
        macAddress: realMacAddress || selectedDevice.id, // Use real MAC if available, otherwise UUID
        deviceType: detectDeviceType(selectedDevice.name),
        hasRealMac: !!realMacAddress, // Flag to indicate if we got real MAC
      };

      console.log('Final parsed device data:', parsedData);

      // Call parent callback
      onDeviceSelect(parsedData);

      // Close modal
      handleClose();
    } catch (err) {
      console.error('Connection error:', err);
      setError('Failed to read device information. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  const detectDeviceType = (name) => {
    const upperName = (name || '').toUpperCase();

    if (upperName.includes('KBEACON') || upperName.includes('KBPRO')) {
      return 'kbeacon';
    } else if (upperName.includes('ELA') || upperName.includes('PUCK')) {
      return 'ela';
    } else if (upperName.includes('BLOG')) {
      return 'blogm2';
    } else if (upperName.includes('S6')) {
      return 's6';
    }

    return 'kbeacon'; // Default
  };

  const handleClose = () => {
    setDevices([]);
    setSelectedDevice(null);
    setError('');
    setScanning(false);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onRequestClose={handleClose}
      modalHeading="Scan for Bluetooth Devices"
      primaryButtonText={connecting ? 'Connecting...' : selectedDevice ? 'Connect Device' : 'Close'}
      secondaryButtonText={selectedDevice ? 'Cancel' : undefined}
      onRequestSubmit={selectedDevice ? handleConnect : handleClose}
      onSecondarySubmit={selectedDevice ? handleClose : undefined}
      primaryButtonDisabled={connecting}
      size="md"
      preventCloseOnClickOutside
    >
      <div style={{ padding: '0 0 24px 0' }}>
        {/* Bluetooth availability warnings */}
        {bluetoothAvailable && !bluetoothAvailable.available && (
          <InlineNotification
            kind="error"
            title="Bluetooth Not Available"
            subtitle={bluetoothAvailable.reason}
            hideCloseButton
            style={{ marginBottom: '16px' }}
          />
        )}

        {bluetoothAvailable && bluetoothAvailable.available && !bluetoothService.isChrome() && (
          <InlineNotification
            kind="warning"
            title="Browser Recommendation"
            subtitle="For best results, use Google Chrome browser."
            hideCloseButton
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* Instructions */}
        <div style={{
          backgroundColor: bgSecondary,
          padding: '16px',
          borderRadius: '4px',
          marginBottom: '24px',
        }}>
          <p style={{ color: textPrimary, margin: '0 0 8px 0', fontWeight: 500 }}>
            How to scan:
          </p>
          <ol style={{ color: textSecondary, fontSize: '0.875rem', margin: 0, paddingLeft: '20px' }}>
            <li>Make sure your sensor is powered on and nearby</li>
            <li>Click "Scan for Devices" below</li>
            <li>Select your sensor from the browser's device list</li>
            <li>Review and connect the device</li>
          </ol>
        </div>

        {/* Error */}
        {error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError('')}
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* Scan Button */}
        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
          <Button
            kind="primary"
            renderIcon={Scan}
            onClick={handleScan}
            disabled={scanning || (bluetoothAvailable && !bluetoothAvailable.available)}
            size="lg"
          >
            {scanning ? 'Scanning...' : 'Scan for Devices'}
          </Button>
        </div>

        {/* Scanning indicator */}
        {scanning && (
          <div style={{
            textAlign: 'center',
            padding: '32px',
            color: textSecondary,
          }}>
            <Bluetooth size={48} style={{ marginBottom: '16px', color: '#0f62fe' }} />
            <p>Looking for devices...</p>
            <p style={{ fontSize: '0.875rem' }}>A device picker will appear. Select your sensor from the list.</p>
          </div>
        )}

        {/* Connecting indicator */}
        {connecting && (
          <div style={{
            textAlign: 'center',
            padding: '24px',
            backgroundColor: bgSecondary,
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            <Bluetooth size={48} style={{ marginBottom: '12px', color: '#0f62fe' }} />
            <p style={{ color: textPrimary, fontWeight: 500, marginBottom: '8px' }}>
              Connecting to device...
            </p>
            <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
              Reading MAC address and device information
            </p>
          </div>
        )}

        {/* Found Devices List */}
        {devices.length > 0 && !connecting && (
          <div>
            <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>
              Found Devices ({devices.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {devices.map((device) => (
                <Tile
                  key={device.id}
                  style={{
                    backgroundColor: selectedDevice?.id === device.id ? '#0f62fe20' : bgPrimary,
                    border: `2px solid ${selectedDevice?.id === device.id ? '#0f62fe' : borderColor}`,
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => handleDeviceClick(device)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <Bluetooth size={24} style={{ color: '#0f62fe' }} />
                        <p style={{ color: textPrimary, fontWeight: 600, margin: 0, fontSize: '1rem' }}>
                          {device.name}
                        </p>
                      </div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', margin: '4px 0 0 36px' }}>
                        Device ID: {device.id}
                      </p>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', margin: '4px 0 0 36px' }}>
                        Type: {detectDeviceType(device.name)}
                      </p>
                    </div>
                    {selectedDevice?.id === device.id && (
                      <Checkmark size={24} style={{ color: '#24a148' }} />
                    )}
                  </div>
                </Tile>
              ))}
            </div>
          </div>
        )}

        {/* No devices found message */}
        {!scanning && devices.length === 0 && !error && (
          <div style={{
            textAlign: 'center',
            padding: '32px',
            color: textSecondary,
          }}>
            <Bluetooth size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p>No devices found yet</p>
            <p style={{ fontSize: '0.875rem' }}>Click "Scan for Devices" to start searching</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BluetoothScanModal;
